/**
 * Verträge der Chart-Daten (AP3.T3.3).
 *
 * Ab hier sind diese Daten die **einzige Wahrheitsquelle** für jede objektiv
 * prüfbare Frage im Tool: Drills, Bewertungen, Szenarien und Handanalyse lesen
 * Frequenzen ausschließlich von hier. Deshalb ist das Schema streng — eine
 * unvollständige Matrix oder eine unbekannte Aktion ist ein Fehler, kein
 * Sonderfall.
 */
/* -------------------------------------------------------------------------
 * Die 169 Zellen der 13x13-Matrix
 * ---------------------------------------------------------------------- */
/** Kartenränge, absteigend — die Achsen des Rasters. */
export const CARD_RANKS = [
    'A',
    'K',
    'Q',
    'J',
    'T',
    '9',
    '8',
    '7',
    '6',
    '5',
    '4',
    '3',
    '2',
];
/**
 * Alle 169 Startblätter in der üblichen Notation, in Rasterreihenfolge:
 * Diagonale = Paare (`AA`), oberhalb = suited (`AKs`), unterhalb = offsuit
 * (`AKo`). Zeile für Zeile, wie das Chart gelesen wird.
 */
export const CHART_HANDS = CARD_RANKS.flatMap((row, rowIndex) => CARD_RANKS.map((column, columnIndex) => {
    if (rowIndex === columnIndex)
        return `${row}${column}`;
    return rowIndex < columnIndex ? `${row}${column}s` : `${column}${row}o`;
}));
/** Anzahl der Zellen. Eine Matrix mit weniger ist unvollständig. */
export const CHART_HAND_COUNT = 169;
/** Schnelle Zugehörigkeitsprüfung. */
const CHART_HAND_SET = new Set(CHART_HANDS);
export function isChartHand(value) {
    return typeof value === 'string' && CHART_HAND_SET.has(value);
}
/**
 * Combo-Gewicht eines Blattes: Paare 6, suited 4, offsuit 12.
 *
 * Wird in T3.4 für die gewichtete Gegenprobe gegen die Caption-Prozente
 * gebraucht; hier steht es, damit beide Seiten dieselbe Zahl verwenden.
 */
export function handComboWeight(hand) {
    if (hand.endsWith('s'))
        return 4;
    if (hand.endsWith('o'))
        return 12;
    return 6;
}
/* -------------------------------------------------------------------------
 * Aktionen
 * ---------------------------------------------------------------------- */
/**
 * Geschlossene Menge der Aktionsarten.
 *
 * Abgeleitet aus den tatsächlich im Buch vorkommenden Bildunterschriften —
 * nicht erfunden und nicht offen gelassen. Freier Text wäre später nicht
 * vergleichbar: „3-bet", „3Bet 10bb" und „3-bet all-in" sind dieselbe Art mit
 * unterschiedlicher Größe. Begründung: ADR-0032.
 */
export const CHART_ACTION_KINDS = [
    'fold',
    'check',
    'call',
    'limp',
    'bet',
    'raise',
    'three_bet',
    'four_bet',
    'five_bet',
    'all_in',
];
export function isChartActionKind(value) {
    return typeof value === 'string' && CHART_ACTION_KINDS.includes(value);
}
/** Stabile Kennung einer Aktion, z. B. `raise@2.5x` oder `fold`. */
export function chartActionKey(action) {
    return action.sizing === null || action.sizing === ''
        ? action.kind
        : `${action.kind}@${action.sizing}`;
}
/** Anzeigename einer Aktion. */
const ACTION_LABELS = {
    fold: 'Fold',
    check: 'Check',
    call: 'Call',
    limp: 'Limp',
    bet: 'Bet',
    raise: 'Raise',
    three_bet: '3-Bet',
    four_bet: '4-Bet',
    five_bet: '5-Bet',
    all_in: 'All-in',
};
export function chartActionLabel(action) {
    const base = ACTION_LABELS[action.kind];
    return action.sizing ? `${base} ${action.sizing}` : base;
}
/* -------------------------------------------------------------------------
 * Spot-Metadaten
 * ---------------------------------------------------------------------- */
/** Tischpositionen im 9-max/6-max-Raster. */
export const CHART_POSITIONS = [
    'UTG',
    'UTG+1',
    'UTG+2',
    'LJ',
    'HJ',
    'CO',
    'BN',
    'SB',
    'BB',
];
export function isChartPosition(value) {
    return typeof value === 'string' && CHART_POSITIONS.includes(value);
}
/** Spielform. */
export const CHART_FORMATS = ['cash', 'mtt'];
/** Leerer Spot — alles unbekannt. */
export const EMPTY_CHART_SPOT = {
    format: null,
    heroPosition: null,
    villainPosition: null,
    stackDepthBb: null,
    actionSequence: null,
    sizings: [],
};
/* -------------------------------------------------------------------------
 * Zustand
 * ---------------------------------------------------------------------- */
/**
 * Lebenszyklus eines Charts.
 *
 * - `raw`        — vom Modell gelesen, ungeprüft. Entsteht in T3.3.
 * - `validated`  — die automatischen Prüfungen aus T3.4 sind bestanden.
 * - `approved`   — menschlich freigegeben. **Nur dieser Zustand ist ab T3.5
 *   nach außen sichtbar.**
 * - `failed`     — Digitalisierung nicht verwertbar; der Grund steht am Chart.
 *   Das entscheidet die Pipeline, nicht ein Mensch.
 * - `unusable`   — ein Mensch hat das Chart in der Review verworfen, mit
 *   Begruendung. Bewusst getrennt von `failed`: Das eine ist ein technischer
 *   Fehlschlag, das andere ein fachliches Urteil. T3.6 muss beides
 *   auseinanderhalten koennen.
 */
export const CHART_STATES = ['raw', 'validated', 'approved', 'failed', 'unusable'];
export function isChartState(value) {
    return typeof value === 'string' && CHART_STATES.includes(value);
}
/**
 * Prüft eine Matrix gegen das Schema.
 *
 * Bewusst **nicht** enthalten: die Frequenzsummen-Prüfung (≈100 % je Hand) und
 * der Abgleich gegen die Caption-Prozente. Beides ist Inhalt von T3.4. Hier
 * geht es allein um strukturelle Vollständigkeit und zulässige Werte.
 */
export function validateChartMatrix(matrix) {
    const issues = [];
    if (!Array.isArray(matrix)) {
        return [{ kind: 'missing-hands', message: 'Die Matrix ist keine Liste von Zellen.' }];
    }
    const seen = new Set();
    for (const raw of matrix) {
        const cell = raw;
        const hand = cell?.hand;
        if (!isChartHand(hand)) {
            issues.push({
                kind: 'unknown-hand',
                message: `Unbekanntes Blatt "${String(hand)}". Erlaubt sind die 169 Blätter des 13x13-Rasters.`,
            });
            continue;
        }
        if (seen.has(hand)) {
            issues.push({ kind: 'duplicate-hand', message: `Blatt "${hand}" kommt mehrfach vor.` });
            continue;
        }
        seen.add(hand);
        const actions = cell?.actions;
        if (!Array.isArray(actions) || actions.length === 0) {
            issues.push({
                kind: 'empty-cell',
                message: `Zelle "${hand}" enthält keine Aktion. Auch reines Fold wird ausgewiesen.`,
            });
            continue;
        }
        for (const entry of actions) {
            const action = entry?.action;
            if (!isChartActionKind(action?.kind)) {
                issues.push({
                    kind: 'unknown-action',
                    message: `Zelle "${hand}": unbekannte Aktionsart "${String(action?.kind)}". ` +
                        `Erlaubt: ${CHART_ACTION_KINDS.join(', ')}.`,
                });
            }
            const percent = entry?.percent;
            if (typeof percent !== 'number' ||
                !Number.isFinite(percent) ||
                percent < 0 ||
                percent > 100) {
                issues.push({
                    kind: 'percent-out-of-range',
                    message: `Zelle "${hand}": Frequenz ${String(percent)} liegt außerhalb 0-100.`,
                });
            }
        }
    }
    const missing = CHART_HANDS.filter((hand) => !seen.has(hand));
    if (missing.length > 0) {
        issues.push({
            kind: 'missing-hands',
            message: `Die Matrix ist unvollständig: ${missing.length} von ${CHART_HAND_COUNT} Blättern fehlen ` +
                `(z. B. ${missing.slice(0, 5).join(', ')}).`,
        });
    }
    return issues;
}
/** Kurzform: Erfüllt die Matrix das Schema? */
export function isValidChartMatrix(matrix) {
    return validateChartMatrix(matrix).length === 0;
}
/* -------------------------------------------------------------------------
 * JSON-Schema der Vision-Antwort
 * ---------------------------------------------------------------------- */
/**
 * Schema, gegen das das Template `task/chart-digitize` antwortet.
 *
 * Das Modell liefert die Matrix und meldet unsichere Bereiche; die
 * Spot-Metadaten kommen **nicht** von hier, sondern deterministisch aus der
 * Bildunterschrift.
 */
/** Die Bausteine der gedruckten Legende: Aktionsbezeichnung und Prozentwert. */
const CHART_LEGEND_VALUES_SCHEMA = {
    type: 'array',
    items: {
        type: 'object',
        properties: {
            art: { type: 'string', enum: [...CHART_ACTION_KINDS] },
            sizing: { type: 'string' },
            prozent: { type: 'number' },
            beschriftung: { type: 'string' },
        },
        required: ['art', 'prozent', 'beschriftung'],
        additionalProperties: false,
    },
};
export const CHART_EXTRACTION_SCHEMA = {
    type: 'object',
    properties: {
        zellen: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    hand: { type: 'string' },
                    aktionen: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                art: { type: 'string', enum: [...CHART_ACTION_KINDS] },
                                sizing: { type: 'string' },
                                prozent: { type: 'number' },
                            },
                            required: ['art', 'prozent'],
                            additionalProperties: false,
                        },
                    },
                },
                required: ['hand', 'aktionen'],
                additionalProperties: false,
            },
        },
        unsicher: { type: 'array', items: { type: 'string' } },
        legende: { type: 'array', items: { type: 'string' } },
        // Die im Bild **gedruckte** Legende mit ihren Prozentwerten. Unabhaengige
        // Beobachtung: Sie wird abgelesen, nicht aus der Matrix hergeleitet.
        legendenwerte: { ...CHART_LEGEND_VALUES_SCHEMA },
        legendenwerte_vorhanden: { type: 'boolean' },
    },
    required: ['zellen', 'unsicher', 'legende', 'legendenwerte', 'legendenwerte_vorhanden'],
    additionalProperties: false,
};
/**
 * Schema der gedruckten Legendenwerte — eigenständig, weil der reine
 * Legenden-Nachzug (AP3.T3.6-fix) dasselbe Format ohne Matrix verwendet.
 */
export const CHART_LEGEND_ONLY_SCHEMA = {
    type: 'object',
    properties: {
        legendenwerte: { ...CHART_LEGEND_VALUES_SCHEMA },
        legendenwerte_vorhanden: { type: 'boolean' },
        unsicher: { type: 'array', items: { type: 'string' } },
    },
    required: ['legendenwerte', 'legendenwerte_vorhanden', 'unsicher'],
    additionalProperties: false,
};
//# sourceMappingURL=chart.js.map